// better-auth's refusals (`{ code, message }`, spec/08 §2.6) → the portal's copy (the `player` namespace). The answer
// never says whether an account exists (spec/08 §2.6): a wrong password and an unknown number read alike. Only the
// upper-case better-auth codes that need the portal's own wording are mapped here; every other failure (a problem
// document's snake_case code, a rate limit, a 5xx, no connection) is read through `useProblem`, so the player gets the
// shared line for that code and the generic line only for a code with no copy of its own.

import { useTranslation } from '@clavian/i18n/react';
import { useCallback } from 'react';

import { problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';

const AUTH_KEYS: Readonly<Record<string, string>> = {
  INVALID_EMAIL_OR_PASSWORD: 'signIn.failure.credentials',
  INVALID_USERNAME_OR_PASSWORD: 'signIn.failure.credentials',
  INVALID_PASSWORD: 'signIn.failure.credentials',
  INVALID_OTP: 'signIn.failure.code',
  OTP_EXPIRED: 'signIn.failure.code',
  OTP_NOT_FOUND: 'signIn.failure.code',
  TOO_MANY_ATTEMPTS: 'signIn.failure.code',
  INVALID_CODE: 'signIn.failure.code',
  INVALID_BACKUP_CODE: 'signIn.failure.code',
  INVALID_TWO_FACTOR_COOKIE: 'signIn.failure.code',
  RATE_LIMITED: 'signIn.failure.rateLimited',
  BANNED_USER: 'signIn.failure.banned',
  // The password was right, and the address it belongs to was never verified: say so, not "something went wrong".
  EMAIL_NOT_VERIFIED: 'signIn.failure.notVerified',
  OTP_DELIVERY_UNAVAILABLE: 'signIn.failure.delivery',
  // Phone sign-in turned off on the Platform: only a page that still showed the phone path gets this.
  PHONE_SIGN_IN_OFF: 'signIn.failure.phoneOff',
  INVALID_PHONE_NUMBER: 'signIn.invalidPhone',
  PREMIUM_RATE_NUMBER: 'signIn.invalidPhone',
  // A number with no account would create a player account at a venue or on the app, not here.
  CONSENT_REQUIRED: 'signIn.failure.noAccount',
};

/**
 * The words for a failed auth request. `overrides` maps a code to the page's own copy key (sign-up says "too short" for
 * `PASSWORD_TOO_SHORT`); a code in neither table is read through the shared problem copy.
 */
export function useAuthFailure(): (error: unknown, overrides?: Readonly<Record<string, string>>) => string {
  const { t } = useTranslation('player');
  const problem = useProblem();
  return useCallback(
    (error, overrides = {}) => {
      const code = problemCode(error);
      const key = code === undefined ? undefined : (overrides[code] ?? AUTH_KEYS[code]);
      return key === undefined ? problem.text(error) : t(key);
    },
    [t, problem],
  );
}
